import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import type { Product } from "../../../types/product";
import { useCart } from "../../../context/CartContext";

const CLEARANCE_THRESHOLD_PCT = 15;
const MAX_VISIBLE = 8;

interface Props {
  products: Product[];
}

function ClearanceCard({ product }: { product: Product }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  const savePct = product.originalPrice
    ? Math.round((1 - product.price / product.originalPrice) * 100)
    : 0;
  const saveDollars = product.originalPrice
    ? Math.round(product.originalPrice - product.price)
    : 0;

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.inStock) return;
    addItem({
      id: product.id,
      variantId: product.variantId,
      name: product.name,
      brand: product.brand,
      price: product.price,
      image: product.image,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };

  return (
    <div
      className="group flex bg-white border border-gray-100 rounded-lg overflow-hidden hover:border-red-300 transition-all duration-200 cursor-pointer"
      onClick={() => navigate(`/shop/${product.id}`)}
    >
      {/* Image column */}
      <div className="relative w-36 shrink-0 bg-gray-50 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
        />
        {/* Big discount badge */}
        <div className="absolute top-2 left-2 flex flex-col items-center justify-center w-12 h-12 rounded-full bg-red-500 text-white shadow-sm">
          <span className="text-[11px] font-black leading-none">-{savePct}%</span>
          <span className="text-[9px] font-semibold leading-none mt-0.5 opacity-90">OFF</span>
        </div>
        {!product.inStock && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <span className="text-[10px] font-bold bg-gray-700 text-white px-2 py-0.5 rounded">Sold Out</span>
          </div>
        )}
      </div>

      {/* Info column */}
      <div className="flex flex-col justify-between p-3.5 flex-1 min-w-0">
        <div>
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">{product.brand}</p>
          <h4 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-2">{product.name}</h4>
          {/* Pricing */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-base font-extrabold text-red-600">${product.price.toLocaleString()}</span>
            <span className="text-xs text-gray-400 line-through">${product.originalPrice!.toLocaleString()}</span>
          </div>
          <p className="text-xs font-bold text-red-500 mt-0.5">
            You save ${saveDollars.toLocaleString()}
          </p>
        </div>

        {/* CTA */}
        <button
          onClick={handleAdd}
          disabled={!product.inStock}
          className={`mt-3 w-full py-2 rounded-md text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
            added
              ? "bg-emerald-500 text-white"
              : product.inStock
              ? "bg-red-500 hover:bg-red-400 text-white"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
        >
          {added ? (
            <span className="flex items-center justify-center gap-1">
              <i className="ri-check-line"></i> Added!
            </span>
          ) : product.inStock ? (
            "Grab the Deal"
          ) : (
            "Sold Out"
          )}
        </button>
      </div>
    </div>
  );
}

export default function ClearanceCorner({ products }: Props) {
  const [expanded, setExpanded] = useState(false);

  const clearanceItems = useMemo(() => {
    return products
      .filter(
        (p) =>
          p.originalPrice &&
          p.originalPrice > p.price &&
          Math.round((1 - p.price / p.originalPrice) * 100) >= CLEARANCE_THRESHOLD_PCT
      )
      .sort((a, b) => {
        const pctA = Math.round((1 - a.price / a.originalPrice!) * 100);
        const pctB = Math.round((1 - b.price / b.originalPrice!) * 100);
        return pctB - pctA;
      });
  }, [products]);

  if (clearanceItems.length === 0) return null;

  const topDiscount = Math.round(
    (1 - clearanceItems[0].price / clearanceItems[0].originalPrice!) * 100
  );
  const totalSavings = clearanceItems.reduce(
    (sum, p) => sum + Math.round(p.originalPrice! - p.price),
    0
  );

  const visible = expanded ? clearanceItems : clearanceItems.slice(0, MAX_VISIBLE);
  const hasMore = clearanceItems.length > MAX_VISIBLE;

  return (
    <div className="mt-10" id="clearance-corner">
      {/* Section divider */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-px bg-gray-100"></div>
        <div className="flex items-center gap-2 text-xs text-gray-400 font-semibold uppercase tracking-widest shrink-0">
          <i className="ri-arrow-down-line text-gray-300"></i>
          clearance below
          <i className="ri-arrow-down-line text-gray-300"></i>
        </div>
        <div className="flex-1 h-px bg-gray-100"></div>
      </div>

      {/* Header block */}
      <div className="rounded-t-xl overflow-hidden bg-gray-900 px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="w-12 h-12 flex items-center justify-center bg-red-500 rounded-lg shrink-0">
            <i className="ri-scissors-cut-fill text-white text-2xl"></i>
          </div>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-white font-extrabold text-lg tracking-tight">
                Clearance Corner
              </h3>
              <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-0.5 rounded-full whitespace-nowrap">
                Up to {topDiscount}% OFF
              </span>
            </div>
            <p className="text-gray-400 text-xs mt-1 leading-relaxed">
              {clearanceItems.length} deeply discounted units — save up to ${totalSavings.toLocaleString()} across all clearance items.{" "}
              <span className="text-red-400 font-semibold">While supplies last.</span>
            </p>
          </div>
        </div>
        {/* Stats row */}
        <div className="flex items-center gap-5 shrink-0">
          <div className="text-center">
            <p className="text-white font-extrabold text-xl leading-none">{clearanceItems.length}</p>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">Items</p>
          </div>
          <div className="w-px h-8 bg-gray-700"></div>
          <div className="text-center">
            <p className="text-red-400 font-extrabold text-xl leading-none">{topDiscount}%</p>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">Max Off</p>
          </div>
          <div className="w-px h-8 bg-gray-700"></div>
          <div className="text-center">
            <p className="text-white font-extrabold text-xl leading-none">${(totalSavings).toLocaleString()}</p>
            <p className="text-gray-500 text-[10px] uppercase tracking-wider mt-0.5">Total Saved</p>
          </div>
        </div>
      </div>

      {/* Urgency strip */}
      <div className="bg-red-500 px-6 py-2 flex items-center gap-2">
        <div className="w-3.5 h-3.5 flex items-center justify-center">
          <i className="ri-time-line text-white text-xs"></i>
        </div>
        <p className="text-white text-xs font-semibold">
          Clearance pricing is available while stock lasts — prices may increase or items may be removed at any time.
        </p>
      </div>

      {/* Product grid */}
      <div className="border border-t-0 border-gray-100 rounded-b-xl p-5 bg-gray-50">
        {/* Category chips */}
        <div className="flex items-center gap-2 flex-wrap mb-5">
          <span className="text-xs text-gray-500 font-medium">All clearance deals — sorted by biggest savings first</span>
          <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
            {clearanceItems.filter((p) => p.inStock).length} In Stock
          </span>
          {clearanceItems.filter((p) => !p.inStock).length > 0 && (
            <span className="bg-gray-200 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
              {clearanceItems.filter((p) => !p.inStock).length} Sold Out
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {visible.map((p) => (
            <ClearanceCard key={p.id} product={p} />
          ))}
        </div>

        {/* Expand / collapse */}
        {hasMore && (
          <div className="mt-5 flex items-center justify-center">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 px-6 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 hover:border-red-300 hover:text-red-600 bg-white transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
              </div>
              {expanded
                ? "Show Less"
                : `See ${clearanceItems.length - MAX_VISIBLE} More Clearance Items`}
            </button>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-5 pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-gray-400 leading-relaxed">
            All clearance items include free shipping and manufacturer warranty where applicable.
          </p>
          <Link
            to="/contact"
            className="text-xs text-red-500 hover:text-red-400 font-semibold transition-colors cursor-pointer whitespace-nowrap shrink-0"
          >
            Questions about clearance pricing? Contact us &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
}
